(function() {
  'use strict';

  const SESSION_KEY_INS_DECLINED = 'ins_declined';
  
  class PackageProtect {
    constructor(element) {
      this.element = element;
      this.insuranceVariantId = element.dataset.variant;
      this.checkbox = element.querySelector('[data-package-protect-checkbox]');
      this.flags = {
        loaded: false,
        insuranceInCart: false,
        is_Checkout: window.Shopify && window.Shopify.Checkout || false
      };
      this.cart = { items: [] };
      this.addInsurance = false;
      this.lineitem_ins = null;
      this.syncing = false;
      this.refreshTimeout = null;
      this.checkboxHandler = null;
      this.syncTimeout = null;
      
      this.init();
    }

    async init() {
      try {
        this.element.setAttribute('data-loaded', 'true');
        this.element.classList.add('loaded');
        
        if (this.flags.is_Checkout) {
          this.moveElement();
        }

        await this.updateCart();
        this.checkFlags();
        
        const userDeclined = sessionStorage.getItem(SESSION_KEY_INS_DECLINED) === 'true';
        const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
        
        if (!userDeclined && !this.flags.insuranceInCart && nonInsuranceItems.length > 0) {
          this.addInsurance = true;
          this.updateCheckboxState(true);
        } else {
          this.addInsurance = this.flags.insuranceInCart;
          this.updateCheckboxState(this.flags.insuranceInCart);
        }
        
        this.setupEventListeners();
        await this.toggleInsuranceInCart();
        this.injectProtectionCSS();
        this.hideProtectionControls();
        this.setupCartObserver();
        this.flags.loaded = true;
      } catch (error) {
        console.error('PackageProtect init error:', error);
        this.element.setAttribute('data-loaded', 'true');
        this.flags.loaded = true;
      }
    }

    setupEventListeners() {
      if (this.checkbox) {
        if (this.checkboxHandler) {
          this.checkbox.removeEventListener('change', this.checkboxHandler);
        }
        
        this.checkboxHandler = async (e) => {
          if (this.syncing) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          }
          
          const wasChecked = e.target.checked;
          
          if (wasChecked) {
            const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
            if (nonInsuranceItems.length === 0) {
              e.target.checked = false;
              this.updateCheckboxState(false);
              return false;
            }
          }
          
          this.addInsurance = wasChecked;
          this.updateCheckboxState(wasChecked);
          sessionStorage.setItem(SESSION_KEY_INS_DECLINED, !this.addInsurance);
          
          setTimeout(() => {
            this.toggleInsuranceInCart();
          }, 0);
          
          return true;
        };
        
        this.checkbox.addEventListener('change', this.checkboxHandler, { passive: false });
        
        this.checkbox.addEventListener('click', (e) => {
          if (this.syncing) {
            e.stopPropagation();
            return false;
          }
        }, { capture: true, passive: false });
      }

      const syncCartState = async () => {
        if (this.syncing) return;
        if (this.syncTimeout) {
          clearTimeout(this.syncTimeout);
        }
        this.syncTimeout = setTimeout(async () => {
          if (this.syncing) return;
          this.syncing = true;
          try {
            await this.updateCart();
            this.checkFlags();
            
            const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
            if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
              await this.removeInsuranceFromCart();
            } else {
              this.updateCheckboxState(this.flags.insuranceInCart);
              this.addInsurance = this.flags.insuranceInCart;
            }
            
            this.updateTotals();
            if (this.flags.is_Checkout) {
              this.updateCheckoutDetails();
            }
            setTimeout(() => {
              this.hideProtectionControls();
            }, 100);
          } catch (error) {
            console.error('Error in syncCartState:', error);
          } finally {
            this.syncing = false;
          }
        }, 500);
      };

      document.addEventListener('cart:updated', syncCartState);
      document.addEventListener('cart:change', syncCartState);
      document.addEventListener('cart:refresh', syncCartState);
      document.addEventListener('theme:cart:update', syncCartState);

      document.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('[data-item-remove]') || 
                         e.target.closest('.cart__item__remove') ||
                         e.target.closest('[data-cart-item-remove]');
        if (removeBtn) {
          const itemKey = removeBtn.getAttribute('data-id') || 
                         removeBtn.closest('[data-id]')?.getAttribute('data-id');
          const isRemovingInsurance = itemKey && this.lineitem_ins && itemKey === this.lineitem_ins.key;
          
          if (window.location.pathname.includes('/cart')) {
            setTimeout(async () => {
              await this.updateCart();
              this.checkFlags();
              
              const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
              if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
                await this.removeInsuranceFromCart();
              }
              
              window.location.reload();
            }, 800);
          } else {
            setTimeout(async () => {
              await this.updateCart();
              this.checkFlags();
              
              const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
              if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
                await this.removeInsuranceFromCart();
              } else if (!isRemovingInsurance) {
                this.updateCheckboxState(this.flags.insuranceInCart);
                this.addInsurance = this.flags.insuranceInCart;
              }
              
              this.updateTotals();
              this.refreshCartDrawer();
              document.dispatchEvent(new CustomEvent('cart:updated'));
            }, 800);
          }
        }
      });
      
      document.addEventListener('change', async (e) => {
        const quantityField = e.target.closest('[data-quantity-field]') || 
                             e.target.closest('input[data-quantity-field]');
        if (quantityField) {
          const quantity = parseInt(quantityField.value) || 0;
          if (quantity === 0) {
            if (window.location.pathname.includes('/cart')) {
              setTimeout(async () => {
                await this.updateCart();
                this.checkFlags();
                
                const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
                if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
                  await this.removeInsuranceFromCart();
                }
                
                window.location.reload();
              }, 1000);
            } else {
              setTimeout(async () => {
                await this.updateCart();
                this.checkFlags();
                
                const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
                if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
                  await this.removeInsuranceFromCart();
                } else {
                  this.updateCheckboxState(this.flags.insuranceInCart);
                  this.addInsurance = this.flags.insuranceInCart;
                }
                
                this.updateTotals();
                this.refreshCartDrawer();
                document.dispatchEvent(new CustomEvent('cart:updated'));
              }, 1000);
            }
          }
        }
      });
      
      document.addEventListener('theme:cart:update', async (e) => {
        if (e.detail && e.detail.quantity === 0) {
          setTimeout(async () => {
            await this.updateCart();
            this.checkFlags();
            
            const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
            if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
              await this.removeInsuranceFromCart();
            } else {
              this.updateCheckboxState(this.flags.insuranceInCart);
              this.addInsurance = this.flags.insuranceInCart;
            }
            
            this.updateTotals();
            if (window.location.pathname.includes('/cart')) {
              window.location.reload();
            } else {
              this.refreshCartDrawer();
            }
          }, 1000);
        }
      });
    }

    updateCheckboxState(checked) {
      if (this.checkbox) {
        this.checkbox.checked = checked;
      }
    }

    injectProtectionCSS() {
      const styleId = 'package-protection-hide-controls';
      if (document.getElementById(styleId)) return;
      
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        [data-cart-item][data-package-protection="true"] [data-quantity-holder],
        [data-cart-item][data-package-protection="true"] [data-item-remove],
        [data-cart-item][data-package-protection="true"] .cart__item__remove,
        [data-cart-item][data-package-protection="true"] [data-popout-quantity],
        [data-cart-item][data-package-protection="true"] .select-popout[data-popout-quantity],
        [data-cart-item][data-package-protection="true"] .cart__item__quantity,
        [data-cart-item][data-package-protection="true"] [data-quantity-field] {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    hideProtectionControls() {
      if (!this.insuranceVariantId || !this.cart || !this.cart.items) return;
      
      this.injectProtectionCSS();
      
      const insuranceItem = this.cart.items.find(item => item.variant_id == this.insuranceVariantId);
      if (!insuranceItem) return;
      
      const cartItems = document.querySelectorAll('[data-cart-item]');
      cartItems.forEach(item => {
        const itemKey = item.getAttribute('data-item') || item.getAttribute('data-id');
        if (itemKey === insuranceItem.key) {
          item.setAttribute('data-package-protection', 'true');
        }
      });
    }

    async updateCart() {
      try {
        const response = await fetch('/cart.js');
        this.cart = await response.json();
        this.checkFlags();
        const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
        if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
          const wasSyncing = this.syncing;
          this.syncing = true;
          await this.removeInsuranceFromCart();
          if (!wasSyncing) {
            this.syncing = false;
          }
        }
        setTimeout(() => {
          this.hideProtectionControls();
        }, 50);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }

    checkFlags() {
      const lineitems_ins = this.cart.items.filter(item => item.variant_id == this.insuranceVariantId);
      this.flags.insuranceInCart = lineitems_ins.length !== 0;
      this.lineitem_ins = lineitems_ins[0] || null;
    }

    updateTotals() {
      const subtotal = this.cart.total_price || 0;
      const cartTotalElements = document.querySelectorAll('[data-cart-total-price]');
      cartTotalElements.forEach((cartTotal) => {
        if (window.themeCurrency) {
          if (subtotal === 0) {
            cartTotal.textContent = window.theme.strings?.free || 'Free';
          } else {
            const format = window.theme.settings?.currency_code_enable 
              ? window.theme.moneyWithCurrencyFormat 
              : window.theme.moneyFormat;
            cartTotal.textContent = window.themeCurrency.formatMoney(subtotal, format);
          }
        } else {
          cartTotal.textContent = this.formatMoney(subtotal);
        }
      });

      const cartTotalAttrs = document.querySelectorAll('[data-cart-total]');
      cartTotalAttrs.forEach((el) => {
        el.setAttribute('data-cart-total', subtotal);
        if (el.textContent && window.themeCurrency) {
          const format = window.theme.settings?.currency_code_enable 
            ? window.theme.moneyWithCurrencyFormat 
            : window.theme.moneyFormat;
          el.textContent = window.themeCurrency.formatMoney(subtotal, format);
        }
      });

    }

    formatMoney(cents) {
      if (window.theme && window.theme.moneyFormat) {
        return window.theme.moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2));
      }
      return '$' + (cents / 100).toFixed(2);
    }

    async toggleInsuranceInCart() {
      if (this.syncing) {
        setTimeout(() => {
          this.toggleInsuranceInCart();
        }, 100);
        return;
      }
      
      this.syncing = true;
      
      try {
        await this.updateCart();
        this.checkFlags();
        
        const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
        
        if (nonInsuranceItems.length === 0 && this.flags.insuranceInCart) {
          await this.removeInsuranceFromCart();
          return;
        }
        
        if (this.addInsurance && nonInsuranceItems.length === 0) {
          this.addInsurance = false;
          this.updateCheckboxState(false);
          return;
        }
        
        const shouldAddIns = this.addInsurance && !this.flags.insuranceInCart;
        const shouldRemoveIns = !this.addInsurance && this.flags.insuranceInCart;

        if (shouldAddIns) {
          await this.addInsuranceToCart();
        } else if (shouldRemoveIns) {
          await this.removeInsuranceFromCart();
        } else {
          this.updateCheckboxState(this.flags.insuranceInCart);
        }
      } catch (error) {
        console.error('Error in toggleInsuranceInCart:', error);
      } finally {
        this.syncing = false;
      }
    }

    async addInsuranceToCart() {
      try {
        const nonInsuranceItems = this.cart.items.filter(item => item.variant_id != this.insuranceVariantId);
        if (nonInsuranceItems.length === 0) {
          this.addInsurance = false;
          this.updateCheckboxState(false);
          return;
        }

        const formData = new FormData();
        formData.append('id', this.insuranceVariantId);
        formData.append('quantity', '1');

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          await this.updateCart();
          this.checkFlags();
          this.updateCheckboxState(this.flags.insuranceInCart);
          this.updateTotals();
          document.dispatchEvent(new CustomEvent('cart:updated'));
          setTimeout(() => {
            this.hideProtectionControls();
            this.setupCartObserver();
          }, 100);
          this.refreshCartDrawer();
        } else {
          await this.updateCart();
          this.checkFlags();
          this.updateCheckboxState(this.flags.insuranceInCart);
        }
      } catch (error) {
        console.error('Error adding insurance to cart:', error);
        await this.updateCart();
        this.checkFlags();
        this.updateCheckboxState(this.flags.insuranceInCart);
      }
    }

    async removeInsuranceFromCart() {
      if (!this.lineitem_ins) {
        await this.updateCart();
        this.checkFlags();
        this.updateCheckboxState(false);
        this.addInsurance = false;
        return;
      }

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.lineitem_ins.key,
            quantity: 0
          })
        });

        if (response.ok) {
          await this.updateCart();
          this.checkFlags();
          this.updateCheckboxState(false);
          this.addInsurance = false;
          this.updateTotals();
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          if (window.location.pathname.includes('/cart')) {
            window.location.reload();
          } else {
            this.refreshCartDrawer();
          }
        } else {
          await this.updateCart();
          this.checkFlags();
          this.updateCheckboxState(this.flags.insuranceInCart);
          this.addInsurance = this.flags.insuranceInCart;
        }
      } catch (error) {
        console.error('Error removing insurance from cart:', error);
        await this.updateCart();
        this.checkFlags();
        this.updateCheckboxState(this.flags.insuranceInCart);
        this.addInsurance = this.flags.insuranceInCart;
      }
    }

    refreshCartDrawer() {
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
      }
      
      this.refreshTimeout = setTimeout(() => {
        if (window.cart && typeof window.cart.getCart === 'function') {
          window.cart.getCart();
        } else if (window.cartDrawer && typeof window.cartDrawer.getCart === 'function') {
          window.cartDrawer.getCart();
        } else if (window.theme && window.theme.sections && window.theme.sections['cart-template']) {
          const cartSection = window.theme.sections['cart-template'];
          if (cartSection && typeof cartSection.getCart === 'function') {
            cartSection.getCart();
          }
        } else if (window.PXUTheme && window.PXUTheme.utils && typeof window.PXUTheme.utils.getCart === 'function') {
          window.PXUTheme.utils.getCart();
        }
        this.refreshCartPage();
      }, 300);
    }

    refreshCartPage() {
      if (!window.location.pathname.includes('/cart')) {
        return;
      }
      
      const itemsHolder = document.querySelector('[data-items-holder]');
      if (!itemsHolder) {
        return;
      }

      const cartUrl = window.theme?.routes?.cart_url || '/cart';
      const timestamp = new Date().getTime();
      fetch(`${cartUrl}?section_id=api-cart-items&_=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          const newItems = doc.querySelector('[data-api-line-items]');
          if (newItems && itemsHolder) {
            itemsHolder.innerHTML = newItems.innerHTML;
          }
          
          const newPrice = doc.querySelector('[data-api-cart-price]');
          const priceHolder = document.querySelector('[data-cart-price-holder]');
          if (newPrice && priceHolder) {
            priceHolder.innerHTML = newPrice.innerHTML;
          }
          
          const cartTitle = document.querySelector('.cart__title');
          if (cartTitle) {
            const itemQtySpan = cartTitle.querySelector('[data-cart-items-qty]');
            if (itemQtySpan) {
              const itemCount = this.cart.item_count || 0;
              if (itemCount === 0) {
                window.location.reload();
                return;
              } else {
                const countText = itemCount === 1 ? ' (1 item)' : ` (${itemCount} items)`;
                itemQtySpan.textContent = countText;
              }
            }
          }
          
          const cartCountElements = document.querySelectorAll('[data-cart-count]');
          cartCountElements.forEach(el => {
            el.textContent = this.cart.item_count || 0;
          });
          
          if (this.cart.item_count === 0) {
            const cartEmpty = document.querySelector('.cart__empty') || 
                            document.querySelector('[data-cart-empty]');
            if (cartEmpty) {
              cartEmpty.style.display = 'block';
            }
            const itemsWrapper = document.querySelector('[data-items-wrapper]');
            if (itemsWrapper) {
              itemsWrapper.style.display = 'none';
            }
          } else {
            const itemsWrapper = document.querySelector('[data-items-wrapper]');
            if (itemsWrapper) {
              itemsWrapper.style.display = '';
            }
            const cartEmpty = document.querySelector('.cart__empty') || 
                            document.querySelector('[data-cart-empty]');
            if (cartEmpty) {
              cartEmpty.style.display = 'none';
            }
          }
          
          setTimeout(() => {
            if (typeof initPackageProtect === 'function') {
              initPackageProtect();
            }
            setTimeout(() => {
              const instances = document.querySelectorAll('package-protect[data-variant]');
              instances.forEach(el => {
                if (el.packageProtectInstance) {
                  el.packageProtectInstance.hideProtectionControls();
                  el.packageProtectInstance.setupCartObserver();
                }
              });
            }, 50);
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }, 150);
        })
        .catch(error => {
          console.error('Error refreshing cart page:', error);
          if (this.cart.item_count === 0) {
            window.location.reload();
          }
        });
    }

    moveElement() {
      const targetElement = document.querySelector('.order-summary__section--total-lines');
      if (targetElement && this.element) {
        targetElement.insertAdjacentElement('beforebegin', this.element);
      }
    }

    setupCartObserver() {
      if (this.cartObserver) return;
      
      this.cartObserver = new MutationObserver(() => {
        setTimeout(() => {
          this.hideProtectionControls();
        }, 100);
      });

      const cartContainer = document.querySelector('[data-items-holder]') || 
                           document.querySelector('[data-items-wrapper]') ||
                           document.querySelector('#cart-drawer') ||
                           document.querySelector('.cart__items');
      
      if (cartContainer) {
        this.cartObserver.observe(cartContainer, {
          childList: true,
          subtree: true
        });
      }
    }

    async updateCheckoutDetails() {
      try {
        const response = await fetch(window.location.href);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const selectors = ['.order-summary__section--product-list', '.order-summary__section--total-lines'];
        selectors.forEach((selector) => {
          const newElement = doc.querySelector(selector);
          const currentElement = document.querySelector(selector);
          if (newElement && currentElement) {
            currentElement.innerHTML = newElement.innerHTML;
          }
        });
      } catch (error) {
        console.error('Error updating checkout details:', error);
      }
    }
  }

  // Initialize all package protect instances
  function initPackageProtect() {
    const packageProtectElements = document.querySelectorAll('package-protect[data-variant]');
    packageProtectElements.forEach((element) => {
      if (!element.packageProtectInstance) {
        element.packageProtectInstance = new PackageProtect(element);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPackageProtect);
  } else {
    initPackageProtect();
  }

  // Re-initialize when cart drawer content is updated
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const newElements = node.querySelectorAll ? node.querySelectorAll('package-protect[data-variant]') : [];
            newElements.forEach((element) => {
              if (!element.packageProtectInstance) {
                element.packageProtectInstance = new PackageProtect(element);
              }
            });
          }
        });
      }
    });
  });

  // Observe cart drawer and cart page for changes
  const cartDrawer = document.querySelector('#cart-drawer');
  const cartPage = document.querySelector('[data-cart-page]');
  
  if (cartDrawer) {
    observer.observe(cartDrawer, { childList: true, subtree: true });
  }
  if (cartPage) {
    observer.observe(cartPage, { childList: true, subtree: true });
  }
})();
